const PREFIX_NUMBER_REGEXP = /(0|33|\+33|\(\+33\))(\s)?/;

const NUMBER_15_DIGITS_REGEXP = /\b\d{14}\b/g;
const NUMBER_10_DIGITS_REGEXP = /\b\d{10}\b/g;
// a regexp to transform 15 digits msisdn as international format: XX X XX XX XX XX XX XX
const FORMAT_15D_AS_INTERNATIONAL_REGEXP = /(\d{2})(\d)(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/;
const FORMAT_NUMBER_BY_PAIR_REGEXP = /(\d{2})(?=\d{2})/g;

const MSISDN_14D_REGEXP = /^\d{14}$/;
const MSISDN_15D_REGEXP = /^\d{15}$/;
const INTERNATIONAL_PHONE_15_REGEXP = /(0|33|\+33|\(\+33\))\d{13}/;
const PHONE_10_REGEXP = /(0|33|\+33|\(\+33\))([1-7,9]\d{8})/;

/**
 * Finds 15-digit numbers in a string, and formats them with spaces following international format XX X XX XX XX XX XX XX with insecables spaces
 * @param {string} text The input text
 * @returns {string} The formatted text
 */
function format15Digits(text: string): string {
    return text.replace(
        FORMAT_15D_AS_INTERNATIONAL_REGEXP,
        '$1\u00a0$2\u00a0$3\u00a0$4\u00a0$5\u00a0$6\u00a0$7\u00a0$8',
    );
}

function format14Digits(text: string): string {
    return text.replace(FORMAT_NUMBER_BY_PAIR_REGEXP, '$1\u00a0');
}

function format12Digits(text: string): string {
    const replacedText = text.replace(PREFIX_NUMBER_REGEXP, '0');

    return replacedText.replace(NUMBER_15_DIGITS_REGEXP, (match) =>
        match.replace(FORMAT_NUMBER_BY_PAIR_REGEXP, '$1\u00a0'),
    );
}

/**
 * Finds 10-digit numbers in a string, replaces +33 with 0, and formats them with insecables spaces
 * @param {string} text The input text
 * @returns {string} The formatted text
 */
function format10Digits(text: string): string {
    const replacedText = text.replace(PREFIX_NUMBER_REGEXP, '0');

    return replacedText.replace(NUMBER_10_DIGITS_REGEXP, (match) =>
        match.replace(FORMAT_NUMBER_BY_PAIR_REGEXP, '$1\u00a0'),
    );
}

/**
 * Utility class for phone numbers format in ECD
 * Because pipes purpose is NOT to be injected through Angular DI, it is not recommanded to directly inject pipes in services and components
 * Then this static utility class is there to be used in pipes used in templates (which is their field of use - PhoneNumberPipe & MultiplePhoneNumberPipe)
 * and in components and services programmatic code.
 */
export class PhoneNumberUtil {
    public static LENGTH_AND_FORMAT_RULE_VALIDATOR_REGEXP = /^[+]?(33|0)(?:\d{9}|\d{13})$/;

    public static getFormattedPhoneWithSpaceRemoval(value: string | null | undefined): string | null | undefined {
        if (!value) {
            return value;
        }
        return PhoneNumberUtil.getFormattedPhone(value.replace(/\s/g, ''));
    }

    /**
     * Gets formatted phone number in 0XXXXXXXXX or 33XXXXXXXXX format to a French format
     * AND Format new msisdn format 15 digits as international format: XX X XX XX XX XX XX XX
     * AND Format new possible msisdn format 14 digits as national format: XX XX XX XX XX XX XX XX
     *  Match : begin 01, 02, 03, 04, 05, 06, 07, 09,
     *  Match : begin 331, 332, 333, 334, 335, 336, 337, 339
     *  Match : begin +331, +332, +333, +334, +335, +336, +337, +339
     *  Match : begin (+33)1, (+33)2, (+33)3, (+33)4, (+33)5, (+33)6, (+33)7, (+33)9
     * ex : 0612345678 (or 33612345678) => 06 12 34 56 78
     * @param {string} value in format 0612345678 or 33612345678 or +33612345678
     * @returns {string} phoneNumber in format 06 12 34 56 78
     */
    public static getFormattedPhone(value: string | null | undefined): string | null | undefined {
        if (!value) {
            return value;
        }

        let matches = value.match(MSISDN_15D_REGEXP);
        if (matches) {
            return format15Digits(value);
        }

        matches = value.match(MSISDN_14D_REGEXP);
        if (matches) {
            return format14Digits(value);
        }

        matches = value.match(INTERNATIONAL_PHONE_15_REGEXP);
        if (matches) {
            return format12Digits(value);
        }

        matches = value.match(PHONE_10_REGEXP);

        if (matches) {
            return format10Digits(value);
        }

        return value;
    }

    /**
     * If phone number start with 0 it is valid if and only if its length is 10 or 14
     * If phone number start with 33 it is valid if and only if its length is 15
     * @param value {string} The phone number to validate
     */
    public static isPhoneNumberLengthAndFormatValid(value: string): boolean {
        return PhoneNumberUtil.LENGTH_AND_FORMAT_RULE_VALIDATOR_REGEXP.test(value);
    }
}
